import { type ComponentPropsWithRef, type MouseEvent, type ReactElement, type Ref } from 'react';

import { classNames } from '../../internal/classNames';
import { useLinkComponent } from '../../internal/LinkProvider';

import Icon, { isPlatformIcon, type IconSource } from '../Icon/Icon';
import Tooltip from '../Tooltip/Tooltip';

const ICON_SIZE = {
    Medium: 16,
    Small: 12,
} as const;

const TOUCH_TARGET_SIZE = {
    Big: 32,
    Medium: 24,
} as const;

type IconActionShared = {
    icon: IconSource;
    size?: keyof typeof ICON_SIZE;
    touchTarget?: keyof typeof TOUCH_TARGET_SIZE;
};

export type IconActionButtonProps = IconActionShared &
    Omit<ComponentPropsWithRef<'button'>, 'children' | 'type'> & {
        href?: undefined;
        ref?: Ref<HTMLButtonElement>;
    };

/** Renders through the host `LinkProvider` component; disabled links render a native `<a>` without `href`. */
export type IconActionLinkProps = IconActionShared &
    Omit<ComponentPropsWithRef<'a'>, 'children'> & {
        href: string;
        disabled?: boolean;
        ref?: Ref<HTMLAnchorElement>;
    };

export type IconActionProps = IconActionButtonProps | IconActionLinkProps;

const IconAction = (props: IconActionProps) => {
    const Link = useLinkComponent();
    const {
        icon,
        size = 'Medium',
        touchTarget = 'Big',
        className = '',
        disabled = false,
        title,
        ...rest
    } = props;
    const classNameValue = classNames({
        'ui-icon-action': true,
        'ui-icon-action--small': size === 'Small',
        'ui-icon-action--medium-target': touchTarget === 'Medium',
        'ui-icon-action--disabled': disabled,
        [className]: Boolean(className),
    });
    const content = (
        <>
            <span className='ui-icon-action__touch-target' />
            {isPlatformIcon(icon) ? (
                <Icon
                    icon={icon}
                    size={ICON_SIZE[size]}
                    className='ui-icon-action__icon icon-primary'
                />
            ) : (
                <Icon
                    icon={icon}
                    size={ICON_SIZE[size]}
                    className='ui-icon-action__icon'
                />
            )}
        </>
    );

    let button: ReactElement<{ ref?: Ref<HTMLElement> }>;

    if ('href' in props && props.href !== undefined) {
        const { href, onClick, ref, ...anchorRest } = rest as Omit<
            IconActionLinkProps,
            keyof IconActionShared | 'disabled' | 'className' | 'title'
        >;

        const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
            if (disabled) {
                e.preventDefault();
                return;
            }
            onClick?.(e);
        };

        const anchorProps = {
            ...anchorRest,
            ref,
            'aria-disabled': disabled || undefined,
            className: classNameValue,
            onClick: handleClick,
            tabIndex: disabled ? -1 : anchorRest.tabIndex,
        };

        button = disabled ? (
            <a {...anchorProps}>{content}</a>
        ) : (
            <Link
                {...anchorProps}
                href={href}>
                {content}
            </Link>
        );
    } else {
        const { ref, ...buttonRest } = rest as Omit<
            IconActionButtonProps,
            keyof IconActionShared | 'disabled' | 'className' | 'title'
        >;

        button = (
            <button
                {...buttonRest}
                ref={ref}
                type='button'
                disabled={disabled}
                className={classNameValue}>
                {content}
            </button>
        );
    }

    if (!title) {
        return button;
    }

    return <Tooltip content={title}>{button}</Tooltip>;
};

IconAction.displayName = 'IconAction';

export default IconAction;
